import React from 'react';
import { ui } from '../../config/uiSizes';
import CollapsibleFindingsSection from './CollapsibleFindingsSection';
import {
    isReconciliationWarning,
    isReconciliationRollup,
    stripReconciliationPrefix,
} from '../../utils/migrationWarnings';
import { toFriendlyName } from '../../utils/formatters';
import { getTypeLabelByApiName } from '../../utils/resourceTypes';

interface NotFullyMigratedBannerProps {
    /** Raw import warnings (e.g. `job3Import.warnings`). Non-reconciliation lines are ignored. */
    warnings?: string[];
    /** Optional extra classes for the outer container. */
    className?: string;
}

/**
 * Friendly-names the leading resource-type token of a per-type reconciliation line so the user never
 * sees a raw `genesyscloud_*` identifier. A per-type line looks like
 * `genesyscloud_routing_queue: requested 3, imported 2, dropped 1 (…)`; only the token before the
 * first `": "` is transformed, the numeric breakdown is preserved verbatim.
 */
function friendlyPerTypeLine(line: string): string {
    const separator = line.indexOf(': ');
    if (separator < 0) {
        return line;
    }

    const rawType = line.slice(0, separator).trim();
    const rest = line.slice(separator + 2);
    const friendlyType = getTypeLabelByApiName(toFriendlyName(rawType));
    return `${friendlyType}: ${rest}`;
}

/**
 * Amber "Not fully migrated" banner that surfaces the backend's end-to-end import reconciliation
 * (Phase 1 item 4). It renders the `[RECONCILIATION]` warnings the Importer appends to
 * `job3Import.warnings` when one or more selected resources did not land in the target org: the
 * top-level rollup ("Not fully migrated: M of Y … landed; Z dropped") as a heading, and each
 * per-resource-type `requested/imported/dropped` breakdown as a list item with a friendly type name.
 * Renders nothing when there are no reconciliation warnings (i.e. everything migrated).
 */
const NotFullyMigratedBanner: React.FC<NotFullyMigratedBannerProps> = ({ warnings, className = '' }) => {
    const reconciliation = (warnings ?? []).filter(isReconciliationWarning);
    if (reconciliation.length === 0) {
        return null;
    }

    const rollup = reconciliation.find(isReconciliationRollup);
    const perType = reconciliation.filter((w) => !isReconciliationRollup(w));

    const rollupText = rollup
        ? stripReconciliationPrefix(rollup)
        : 'Some selected resources did not land in the target organization.';

    return (
        <CollapsibleFindingsSection
            variant="amber"
            title="Not fully migrated"
            iconPath="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            {...(perType.length > 0 ? { count: perType.length } : {})}
            sectionId="not-fully-migrated"
            className={className}
            summary={rollupText}
        >
            <p className={`${ui.text.xs} text-amber-800 dark:text-amber-200 ${ui.mb.sm} break-words`}>
                {rollupText}
            </p>

            {perType.length > 0 && (
                <ul className={`flex flex-col ${ui.gap.xs}`}>
                    {perType.map((warning, index) => (
                        <li
                            key={index}
                            className={`flex items-start ${ui.gap.xs} ${ui.text.xs} text-amber-700 dark:text-amber-300`}
                        >
                            <span className="text-amber-500 dark:text-amber-400 shrink-0" aria-hidden="true">
                                •
                            </span>
                            <span className="break-words">{friendlyPerTypeLine(stripReconciliationPrefix(warning))}</span>
                        </li>
                    ))}
                </ul>
            )}

            <p className={`${ui.text.xxs} text-amber-600 dark:text-amber-400 mt-2 break-words`}>
                Re-import or recreate the dropped resources in the target organization, then re-run the
                migration. Review the import results for the full per-resource reason and mitigation.
            </p>
        </CollapsibleFindingsSection>
    );
};

export default NotFullyMigratedBanner;
